import { useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router";
import { styled } from "styled-components";

import { Spinner } from "common/components";
import { TOPICS_FOR_COLLECTION_LIMIT } from "common/constants/limit";
import { Status } from "common/types/status";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useTrack } from "common/utilities/useTrack";
import { clearSingle, fetchCollectionForViewing } from "domains/collection/actions";
import { CollectionPage } from "domains/collection/components/CollectionPage";
import {
  createIsCollectionClientSideSelector,
  getCollectionForViewing,
  getCollectionForViewingStatus,
  getCollectionForViewingTopicsMeta,
} from "domains/collection/selectors";
import { getSiteLanguage } from "domains/language/selectors";
import { activateModal } from "domains/modal/actions";
import { fetchTopicsForCollection } from "entities/topics/actions";
import { createTopicsSelector } from "entities/topics/selectors";

const StyledSpinner = styled(Spinner)`
  // this is to center the spinner (half the screen - half the spinners width)
  margin-left: calc(50% - 32px);
  margin-top: 10rem;
  color: ${({ theme }) => theme.colors.fillPrimary};
`;

export const LoadCollectionPage = () => {
  const dispatch = useDispatch();
  const go = useGo();
  const { collectionId } = useParams();
  const collection = useSelector(getCollectionForViewing);
  const status = useSelector(getCollectionForViewingStatus);
  const topicsMeta = useSelector(getCollectionForViewingTopicsMeta);
  const siteLanguage = useSelector(getSiteLanguage);
  const track = useTrack();

  if (collectionId === undefined)
    throw new Error(`[LoadCollectionPage] 'collectionId' query parameter is '${collectionId}'`);

  const isClientSide = useSelector(createIsCollectionClientSideSelector(collectionId));
  const topicsSelector = useMemo(() => createTopicsSelector(collection?.topic_ids ?? []), [collection?.topic_ids]);
  const siteLanguageRef = useRef(siteLanguage);

  const topics = useSelector(topicsSelector);

  const offset = topicsMeta?.offset ?? 20;

  useEffect(() => {
    if (siteLanguage === siteLanguageRef.current && collection?.id === collectionId) return;
    siteLanguageRef.current = siteLanguage;
    dispatch(fetchCollectionForViewing(collectionId));
  }, [collection?.id, collectionId, dispatch, siteLanguage]);

  useEffect(() => {
    if (siteLanguage === siteLanguageRef.current && isClientSide) return;
    siteLanguageRef.current = siteLanguage;
    dispatch(fetchTopicsForCollection({ id: collectionId, limit: TOPICS_FOR_COLLECTION_LIMIT, offset: 0 }));
  }, [collectionId, dispatch, isClientSide, siteLanguage]);

  useEffect(() => {
    if (status !== Status.Error) return;
    dispatch(clearSingle());
    dispatch(
      activateModal({ id: "Error", params: { localisedMessageKey: "app.collection.unavailable.error_message" } }),
    );
    go("/create?mode=collection", { replace: true });
  }, [dispatch, status, go]);

  useEffect(() => {
    if (collectionId === collection?.id)
      track({
        entityId: collection?.id,
        entityName: collection?.name,
        entityType: "Collection",
        name: "Browse page",
        page: "Collection",
      });
  }, [collection?.id, collection?.name, collectionId, track]);

  const loadNextPage = () =>
    dispatch(fetchTopicsForCollection({ id: collectionId, limit: TOPICS_FOR_COLLECTION_LIMIT, offset }));

  return collection === undefined || collection.id !== collectionId ? (
    <StyledSpinner />
  ) : (
    <CollectionPage collection={collection} loadNextPage={loadNextPage} topics={topics} topicsMeta={topicsMeta} />
  );
};
